import React, { useEffect, useContext, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { TableContext } from './context';
import Row from './row';
import VirtualizedGrid from '../virtualizedGrid';

const Body = React.forwardRef((props, ref) => {
    const {
        columns,
        height,
        width,
        virtualized,
        prerender,
        className,
        style,
        rowClassName,
        rowStyle,
        cellClassName,
        cellStyle,
        rowHeight,
        scrollX,
        scrollY,
        onScroll,
        expandRowHeight,
        expandRowWidth,
        expandRowRender
    } = props;
    const {
        ScrollbarWidth,
        rowKey,
        pageData,
        scrollTop,
        expandedRows,
        expandRowShadow,
        draggingRow,
        draggingRowIndex
    } = useContext(TableContext);

    const showScrollY = scrollY && !className?.includes('hidden-scrollbar-y');
    const columnWidth = useCallback((col, index) => (
        index === columns.length - 1 && showScrollY ? col.width - ScrollbarWidth : col.width
    ), [columns, showScrollY, ScrollbarWidth]);
    const gridRowHeight = useCallback((row, rowIndex) => (
        (rowHeight?.(row, rowIndex) || 0) +
        (expandedRows.includes(row[rowKey]) && expandRowHeight?.(row, rowIndex) || 0)
    ), [rowKey, rowHeight, expandedRows, expandRowHeight]);

    const expandGridColumnWidth = useCallback(() => (
        expandRowWidth || (showScrollY ? width - ScrollbarWidth - 1 : width)
    ), [expandRowWidth, showScrollY, width, ScrollbarWidth]);
    const expandRowRef = useRef();
    useEffect(() => {
        expandRowRef.current?.scrollTo({ top: scrollTop });
    }, [scrollTop]);

    return !!columns?.length && (
        <div className="relative" style={{ height, width }}>
            {!!expandRowRender && (
                <VirtualizedGrid
                    ref={expandRowRef}
                    virtualized={virtualized}
                    prerender={prerender}
                    width={expandGridColumnWidth()}
                    height={scrollX ? height - ScrollbarWidth - 1 : height}
                    columns={['']}
                    rows={pageData}
                    columnWidth={expandGridColumnWidth}
                    rowHeight={gridRowHeight}
                    className="absolute top-0 hidden-scrollbar"
                    onScroll={(e) => {
                        const { target: { scrollTop: top } } = e;
                        if (top !== scrollTop) {
                            onScroll?.(e);
                            ref.current.scrollTo({ top });
                        }
                    }}
                    rowRender={({ row, rowIndex }) => {
                        const currRowHeight = rowHeight?.(row, rowIndex) || 0;
                        const currExpandRowHeight = expandedRows.includes(row[rowKey]) &&
                            expandRowHeight?.(row, rowIndex) || 0;
                        return (
                            <div
                                className={clsx(
                                    `relative table-row table-row-${row[rowKey]}`,
                                    { 'z-20': draggingRow && draggingRow === row[rowKey] },
                                    { transition: draggingRow && draggingRow !== row[rowKey] }
                                )}
                                style={{ width: expandGridColumnWidth() }}
                            >
                                <div style={{ height: currRowHeight }} />
                                {!!currExpandRowHeight && (
                                    <>
                                        <div
                                            className={clsx(
                                                'relative z-10 border-b overflow-hidden',
                                                'bg-white dark:bg-black-light02',
                                                { 'border-gray': !expandRowShadow }
                                            )}
                                            style={{ height: currExpandRowHeight }}
                                        >
                                            {expandRowRender?.(row, rowIndex)}
                                        </div>
                                        {expandRowShadow && (
                                            <>
                                                <div
                                                    // eslint-disable-next-line max-len
                                                    className="shadow-[0px_10px_8px_-8px_rgba(0,0,0,0.15),0px_-10px_8px_-8px_rgba(0,0,0,0.15)]
                                                        w-[calc(100%_+_24px)] absolute top-0
                                                        z-10 pointer-events-none -left-3"
                                                    style={{ height: currRowHeight }}
                                                />
                                                <div
                                                    // eslint-disable-next-line max-len
                                                    className="shadow-[inset_0px_-10px_8px_-8px_rgba(0,0,0,0.15)]
                                                        w-full absolute bottom-0
                                                        z-10 pointer-events-none"
                                                    style={{ height: currExpandRowHeight }}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    }}
                    prerenderRowIndexes={[draggingRowIndex]}
                />
            )}
            <VirtualizedGrid
                ref={ref}
                virtualized={virtualized}
                prerender={prerender}
                width={width}
                height={height}
                columns={columns}
                rows={pageData}
                columnWidth={columnWidth}
                rowHeight={gridRowHeight}
                className={clsx(
                    'overflow-scroll',
                    { 'hidden-scrollbar-x': !scrollX },
                    { 'pointer-events-auto cursor-grab': draggingRow },
                    className
                )}
                style={style}
                onScroll={onScroll}
                rowRender={({ row, rowIndex, columns: showColumns }) => {
                    const currRowHeight = rowHeight?.(row, rowIndex) || 0;
                    const currExpandRowHeight = expandedRows.includes(row[rowKey]) &&
                        expandRowHeight?.(row, rowIndex) || 0;
                    return (
                        <div
                            className={clsx(
                                `ralative table-row table-row-${row[rowKey]}`,
                                { 'pointer-events-none': draggingRow },
                                { 'z-10': draggingRow && draggingRow === row[rowKey] },
                                { transition: draggingRow && draggingRow !== row[rowKey] }
                            )}
                        >
                            <Row
                                columns={showColumns}
                                row={row}
                                rowIndex={rowIndex}
                                height={currRowHeight}
                                className={clsx(
                                    {
                                        'border-b': rowIndex !== pageData.length - 1 ||
                                            !scrollY || currExpandRowHeight
                                    },
                                    { 'border-b-gray': currExpandRowHeight },
                                    rowClassName?.(row, rowIndex)
                                )}
                                style={rowStyle?.(row, rowIndex)}
                                cellClassName={cellClassName}
                                cellStyle={cellStyle}
                                scrollY={scrollY}
                                showScrollY={showScrollY}
                            />
                            {!!currExpandRowHeight && (
                                <div
                                    className="border-b border-gray"
                                    style={{ height: currExpandRowHeight }}
                                />
                            )}
                        </div>
                    );
                }}
                prerenderRowIndexes={[draggingRowIndex]}
            />
            {scrollX && (
                <div className="h-full absolute top-0">
                    <div
                        className="absolute bottom-0 border-t border-gray
                            pointer-events-none"
                        style={{ width, height: ScrollbarWidth + 1 }}
                    />
                </div>
            )}
            {showScrollY && (
                <div className="h-full absolute top-0 right-0">
                    <div
                        className="h-full absolute right-0 border-l border-gray
                            pointer-events-none"
                        style={{ width: ScrollbarWidth + 1 }}
                    />
                </div>
            )}
        </div>
    );
});

export default Body;
